import { Link } from 'react-router-dom';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import HomeIcon from '../Components/home/HomeIcon';
import InnerEnquiryForm from '../Components/inner/InnerEnquiryForm';
import ComingSoon from './ComingSoon';
import Seo from '../Components/Seo';
import { innerPages, innerCtas } from '../data/innerPages';
import { stories } from '../data/homeContent';
import { mainNav } from '../data/siteNav';
import { bannerImages } from '../data/bannerImages';
import { breadcrumbSchema, getPageSeo, organizationSchema } from '../data/seo';
import '../assests/css/inner-page.css';

function getSiblings(path) {
  for (const item of mainNav) {
    const children = [...(item.children || [])];
    if (item.dropdownCta) children.push(item.dropdownCta);
    if (item.to === path) return [];
    if (children.some((child) => child.to === path)) {
      return children.filter((child) => child.to !== path).slice(0, 6);
    }
  }
  return [];
}

function InnerPage({ path, title, parent }) {
  const page = innerPages[path];

  if (!page) {
    return <ComingSoon title={title} parent={parent} path={path} />;
  }

  const siblings = getSiblings(path);
  const heroImage = page.image ? bannerImages[page.image] : null;
  const heroBadge = page.badge || page.outcomes?.[0];
  const seo = getPageSeo(path, { title: page.kicker || title, description: page.lead });

  return (
    <div className="inner-page">
      <Seo
        path={path}
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        schema={[organizationSchema, breadcrumbSchema(path, page.kicker || title)]}
      />
      <Header />
      <main>
        <section className={`inner-hero${heroImage ? '' : ' is-plain'}`}>
          <div className={`container inner-hero-inner${heroImage ? '' : ' is-plain'}`}>
            <div className="inner-hero-copy">
              <p className="inner-crumb">
                {page.parent ? `${page.parent} / ` : ''}
                {page.kicker}
              </p>
              <p className="inner-kicker">{page.kicker}</p>
              <h1>{page.title}</h1>
              <p className="inner-lead">{page.lead}</p>
              <div className="inner-actions">
                <Link to={page.primary.to} className="inner-btn inner-btn-primary">
                  {page.primary.label}
                </Link>
                <Link to={page.secondary.to} className="inner-btn inner-btn-ghost">
                  {page.secondary.label}
                </Link>
              </div>
            </div>
            {heroImage ? (
              <div className="inner-hero-media">
                <img src={heroImage} alt="" />
                {heroBadge ? (
                  <div className="inner-hero-float">
                    <strong>{heroBadge.value}</strong>
                    <span>{heroBadge.label}</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        {page.features?.length ? (
          <section className="inner-section inner-section-white">
            <div className="container">
              <div className="inner-section-head">
                <p className="inner-kicker">WHAT YOU GET</p>
                <h2>Built for the school week, not a slide deck</h2>
              </div>
              <div className="inner-feature-grid">
                {page.features.map((item) => (
                  <article key={item.title} className="inner-feature">
                    <span className="inner-icon">
                      <HomeIcon name={item.icon} size={20} />
                    </span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {page.outcomes?.length ? (
          <section className="inner-section inner-section-cream">
            <div className="container inner-outcome-grid">
              {page.outcomes.map((item) => (
                <article key={item.label} className="inner-outcome">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {page.links?.length ? (
          <section className="inner-section inner-section-white">
            <div className="container">
              <div className="inner-section-head">
                <p className="inner-kicker">EXPLORE</p>
                <h2>Choose the part of Bright Study you want to see first</h2>
              </div>
              <div className="inner-card-grid">
                {page.links.map((item) => (
                  <Link key={item.to} to={item.to} className="inner-card">
                    <span className="inner-icon">
                      <HomeIcon name={item.icon} size={20} />
                    </span>
                    <h3>{item.label}</h3>
                    <p>{item.text}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {page.articles?.length ? (
          <section className="inner-section inner-section-white">
            <div className="container">
              <div className="inner-article-grid">
                {page.articles.map((item) => (
                  <article key={item.title} className="inner-article">
                    <span>{item.tag}</span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {page.people?.length ? (
          <section className="inner-section inner-section-white">
            <div className="container inner-people-grid">
              {page.people.map((item) => (
                <article key={item.name} className="inner-person">
                  <h3>{item.name}</h3>
                  <span>{item.role}</span>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {page.stories ? (
          <section className="inner-section inner-section-white">
            <div className="container inner-card-grid">
              {stories.map((item) => (
                <article key={item.title} className="inner-card">
                  <span className="inner-icon">
                    <HomeIcon name={item.icon} size={20} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {page.form ? (
          <section className="inner-section inner-section-cream">
            <div className="container inner-form-wrap">
              <div className="inner-section-head">
                <p className="inner-kicker">WRITE TO US</p>
                <h2>We read every message</h2>
              </div>
              <InnerEnquiryForm type={page.form} />
            </div>
          </section>
        ) : null}

        {siblings.length ? (
          <section className="inner-section inner-section-cyan">
            <div className="container">
              <div className="inner-section-head">
                <p className="inner-kicker">MORE IN {page.parent?.toUpperCase() || 'BRIGHT STUDY'}</p>
                <h2>Keep exploring</h2>
              </div>
              <div className="inner-related">
                {siblings.map((item) => (
                  <Link key={item.to} to={item.to}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className="inner-cta">
          <div className="container inner-cta-panel">
            <div>
              <p className="inner-kicker">SEE BRIGHT STUDY IN ACTION</p>
              <h2>Ready to build a future-ready school?</h2>
              <p>A walkthrough is the fastest way to see how the modules fit your campus.</p>
            </div>
            <div className="inner-actions">
              <Link to={innerCtas.demo.to} className="inner-btn inner-btn-primary">
                {innerCtas.demo.label}
              </Link>
              <Link to={innerCtas.sales.to} className="inner-btn inner-btn-ghost">
                {innerCtas.sales.label}
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default InnerPage;
